/**
 * Pure rules for drafting a custom scenario, kept apart from the builder so
 * they are testable. The server checks every definition again; these exist so
 * the form starts sensibly and names stay valid while the user types.
 */
import type {
  CustomScenario,
  CustomScenarioDefinition,
  CustomScenarioLever,
  CustomScenarioOutput,
  ScenarioValueUnit,
  WhatIfMetric,
} from "../../../services/api";

/** Formula functions; a metric, input or result may not take their names. */
const RESERVED = new Set(["min", "max", "abs"]);
const MAX_NAME = 40;

export const LIMITS = { metrics: 8, levers: 8, outputs: 8, breakdowns: 6 } as const;

export function emptyDefinition(contextId: string): CustomScenarioDefinition {
  return {
    contextId,
    title: "",
    question: "",
    summary: "",
    icon: "calculator",
    metrics: [],
    levers: [],
    outputs: [],
    breakdowns: [],
    defaultPeriod: "year_to_date",
  };
}

export function definitionOf(scenario: CustomScenario): CustomScenarioDefinition {
  return {
    contextId: scenario.contextId,
    title: scenario.title,
    question: scenario.question,
    summary: scenario.summary,
    icon: scenario.icon,
    metrics: scenario.metrics.map((item) => ({ ...item })),
    levers: scenario.levers.map((item) => ({ ...item })),
    outputs: scenario.outputs.map((item) => ({ ...item })),
    breakdowns: [...scenario.breakdowns],
    defaultPeriod: scenario.defaultPeriod,
  };
}

/** Every name a formula can already read. */
export function takenNames(definition: CustomScenarioDefinition): Set<string> {
  return new Set([
    ...definition.metrics.map((item) => item.name),
    ...definition.levers.map((item) => item.id),
    ...definition.outputs.map((item) => item.id),
  ]);
}

/**
 * A formula name from a label: "Bill rate ($/h)" becomes bill_rate_h. Always
 * starts with a letter, is never a function name and never one already used.
 */
export function nameFor(label: string, taken: Set<string>, fallback = "value"): string {
  let base = label
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, MAX_NAME);
  if (!/^[a-z]/.test(base)) base = `${fallback}${base ? `_${base}` : ""}`.slice(0, MAX_NAME);
  if (RESERVED.has(base)) base = `${base}_value`;
  let name = base;
  for (let suffix = 2; taken.has(name); suffix += 1) {
    const tail = `_${suffix}`;
    name = `${base.slice(0, MAX_NAME - tail.length)}${tail}`;
  }
  return name;
}

/** Keeps a typed name valid as the user edits it, without moving the caret oddly. */
export function cleanName(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/^[^a-z]+/, "")
    .slice(0, MAX_NAME);
}

export function valueUnitFor(metric: WhatIfMetric): ScenarioValueUnit {
  const unit = `${metric.unit ?? ""} ${metric.member}`.toLowerCase();
  if (/hour|hrs\b/.test(unit)) return "hours";
  if (/usd|eur|gbp|currency|\$|amount|revenue|cost|price|margin|ebitda/.test(unit)) {
    return "currency";
  }
  if (/percent|pct|%|rate\b/.test(unit)) return "percent";
  return "count";
}

/**
 * Adds a metric. The first one also brings a starter input and result, so a
 * new scenario is runnable straight away and shows how formulas read names.
 */
export function withMetric(
  definition: CustomScenarioDefinition,
  metric: WhatIfMetric,
): CustomScenarioDefinition {
  const taken = takenNames(definition);
  const name = nameFor(metric.label, taken, "metric");
  taken.add(name);
  const next: CustomScenarioDefinition = {
    ...definition,
    metrics: [...definition.metrics, { name, member: metric.member, label: metric.label }],
  };
  if (definition.levers.length > 0 || definition.outputs.length > 0) return next;

  const leverId = nameFor(`${metric.label} change`, taken, "change");
  taken.add(leverId);
  const lever: CustomScenarioLever = {
    ...newLever(next, `${metric.label} change`),
    id: leverId,
  };
  const output: CustomScenarioOutput = {
    id: nameFor(`${metric.label} in scenario`, taken, "result"),
    label: `${metric.label} in scenario`,
    unit: valueUnitFor(metric),
    formula: `${name} * (1 + ${leverId} / 100)`,
    higherIsBetter: null,
    emphasis: true,
  };
  return { ...next, levers: [lever], outputs: [output] };
}

export function newLever(
  definition: CustomScenarioDefinition,
  label = "New input",
): CustomScenarioLever {
  return {
    id: nameFor(label, takenNames(definition), "input"),
    label,
    unit: "percent",
    minimum: -50,
    maximum: 50,
    step: 1,
    default: 5,
    neutral: 0,
    help: "",
    group: "driver",
  };
}

export function newOutput(definition: CustomScenarioDefinition): CustomScenarioOutput {
  return {
    id: nameFor("New result", takenNames(definition), "result"),
    label: "New result",
    unit: definition.outputs[0]?.unit ?? "currency",
    formula: "",
    higherIsBetter: null,
    emphasis: definition.outputs.length === 0,
  };
}

/** The names a result's formula may read: metrics, inputs and earlier results. */
export function namesFor(definition: CustomScenarioDefinition, outputIndex: number): string[] {
  return [
    ...definition.metrics.map((item) => item.name),
    ...definition.levers.map((item) => item.id),
    ...definition.outputs.slice(0, outputIndex).map((item) => item.id),
  ];
}

/** Moves an item up or down, leaving the list untouched at either end. */
export function moved<T>(items: T[], index: number, offset: -1 | 1): T[] {
  const target = index + offset;
  if (target < 0 || target >= items.length) return items;
  const next = [...items];
  const [item] = next.splice(index, 1);
  next.splice(target, 0, item as T);
  return next;
}

/** Problems worth catching before a round trip. The server checks the rest. */
export function draftProblems(definition: CustomScenarioDefinition): string[] {
  const problems: string[] = [];
  if (definition.title.trim().length < 3) problems.push("Give the scenario a name.");
  if (definition.metrics.length === 0) problems.push("Add at least one certified metric.");
  if (!definition.levers.some((item) => item.group === "driver")) {
    problems.push("Add at least one input to test.");
  }
  if (definition.outputs.length === 0) problems.push("Add at least one result.");
  definition.outputs.forEach((item) => {
    if (!item.formula.trim()) problems.push(`Write a formula for ${item.label || "each result"}.`);
  });
  return problems;
}

export interface FieldProblems {
  /** Messages keyed by the field they belong to, such as outputs.0.formula. */
  byField: Map<string, string>;
  /** Messages that belong to the scenario as a whole. */
  general: string[];
}

/**
 * Sorts the server's field errors into the form. Request validation prefixes
 * fields with "body" and reports rules across fields against the body itself.
 */
export function fieldProblems(errors: { field: string; message: string }[]): FieldProblems {
  const byField = new Map<string, string>();
  const general: string[] = [];
  for (const { field, message } of errors) {
    const path = field.replace(/^body\.?/, "");
    const text = message.replace(/^Value error, /, "");
    if (!path) general.push(text);
    else if (!byField.has(path)) byField.set(path, text);
  }
  return { byField, general };
}
